import { ChangeEvent, useCallback, useRef, useState } from 'react';
import Hammer from 'react-hammerjs';
import AvatarEditor from 'react-avatar-editor';

import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { useUpdateComponent } from 'approot/activity-builder/activity-builder.hooks';
import { useContextualSurface } from 'approot/shared/ally/contextual.hooks';
import { useImageDimensionEffect } from 'approot/shared/images/images.hooks';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/loading-button';
import IconClose from 'approot/shared/icons/icon-close';
import {
  SettingsComponentMenuButton,
  SettingsComponentMenuHr,
} from 'approot/activity-builder/settings/settings-component-menu.style';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import Range from 'lib/form/range/range';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { ComponentDimensions } from '../shared/component-height/component-dimensions';
import { Props } from './image.constants';
import {
  FILL,
  FIT,
  FLIP_HORIZONTAL,
  FlipMode,
  ImagePicker,
  ImageState,
  InitialImageSize,
  Mode,
  RANGE_MAX,
  RANGE_MIN,
  RANGE_STEP,
} from './image-crop.constants';
import {
  clamp,
  getInitialSizeValues,
  prepareCropData,
} from './image-crop.utils';
import * as Styled from './image-crop.style';
import { ImageContainer } from './image.style';
import { getBaseImagePayload } from './image.utils';
import { ComponentCaptionEdit } from '../shared/component-caption/component-caption-edit';
import { JSONObject } from 'global/graphql/scalars.type';
import { CONTENT_IMAGE } from 'global/constants';

const ImageCrop = (
  props: Props & {
    canFlip: boolean;
    onCropComplete: () => void;
    onCaptionChange: (text: JSONObject) => void;
  }
) => {
  const [canCrop, setCanCrop] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isFlipping, setIsFlipping] = useState<boolean>(false);
  const [mode, setMode] = useState<Mode>(
    !props.properties.cropData ? FIT : FILL
  );

  const [imagePicker, setImagePicker] = useState<ImagePicker>({
    width: 0,
    height: 0,
    isReady: false,
  });

  const [imageState, setImageState] = useState<ImageState>({
    imageWidth: 0,
    imageHeight: 0,
    x: 0,
    y: 0,
    zoom: 0,
  });

  const { styles, attributes, setReferenceElement, setPopperElement } =
    useContextualSurface(
      imagePicker.isReady,
      () => {
        setCroppingComponentId('');
      },
      {
        offset: [0, 0],
        placement: 'top-end',
        fallbackPlacements: ['top-start'],
      }
    );

  const { setCroppingComponentId } = useActivityBuilderContext();
  const { updateComponent, loading } = useUpdateComponent();

  const {
    id,
    sectionId,
    properties,
    stimulusResourceProperties,
    canFlip,
    onCropComplete,
    onCaptionChange,
  } = props;

  const flip = (properties.image?.data?.flip as FlipMode) || null;
  const cropData = properties.cropData;
  const stimulus = stimulusResourceProperties;
  const imageUrl =
    stimulus?.image?.original || properties.image?.original || '';

  const handleHeightUpdate = useCallback(
    (props: {
      imageState: ImageState;
      imagePicker: ImagePicker;
      mode: Mode;
    }) => {
      updateComponent({
        sectionId,
        componentId: id,
        componentData: {
          properties: {
            ...getBaseImagePayload(properties),
            cropData:
              props.mode === FIT
                ? null
                : prepareCropData(props.imageState, props.imagePicker),
            dimensionData: {
              width: props.imagePicker.width,
              height: Math.floor(props.imagePicker.height),
            },
          },
        },
      });
    },
    [id, properties, sectionId, updateComponent]
  );

  const handleCropImage = useCallback(() => {
    setIsSaving(true);
    updateComponent({
      sectionId,
      componentId: id,
      componentData: {
        properties: {
          ...getBaseImagePayload(properties),
          cropData:
            mode === FIT ? null : prepareCropData(imageState, imagePicker),
        },
      },
    })
      .then(onCropComplete)
      .finally(() => {
        setIsSaving(false);
      });
  }, [
    id,
    sectionId,
    properties,
    mode,
    imageState,
    imagePicker,
    updateComponent,
    onCropComplete,
  ]);

  const handleFlipImage = useCallback(
    (flip: string | null) => {
      setIsFlipping(true);
      updateComponent({
        sectionId,
        componentId: id,
        componentData: {
          properties: {
            ...getBaseImagePayload(properties),
            image: properties.image
              ? {
                  key: properties.image.key,
                  fileName: properties.image.fileName,
                  imageType: CONTENT_IMAGE,
                  data: {
                    flip,
                  },
                }
              : null,
          },
        },
      }).finally(() => {
        setIsFlipping(false);
      });
    },
    [id, sectionId, properties, updateComponent]
  );

  const setImagePickerSize = useCallback(
    (width: number, height: number) => {
      setImagePicker({
        width,
        height,
        isReady: true,
      });
    },
    [setImagePicker]
  );

  const initialiseImageState = useCallback(
    (size: InitialImageSize) => {
      if (imagePicker.isReady) return;

      setImageState({
        imageWidth: size.naturalWidth,
        imageHeight: size.naturalHeight,
        x: cropData ? cropData.x : 0,
        y: cropData ? cropData.y : 0,
        zoom: cropData ? cropData.zoom : 1,
      });
      setImagePickerSize(size.containerWidth, size.containerHeight);
    },
    [cropData, imagePicker.isReady, setImagePickerSize]
  );

  return (
    <Styled.ImageCrop ref={setReferenceElement}>
      {imagePicker.isReady && (
        <Styled.ImageCropSettings
          ref={setPopperElement}
          role="dialog"
          tabIndex={-1}
          style={styles.popper}
          {...attributes.popper}
        >
          <Styled.ImageCropSettingsInner>
            <Styled.ImageCropSettingsFields>
              <TooltipWrapper hoverText="Cancel">
                <SettingsComponentMenuButton
                  type="button"
                  $isSmall
                  disabled={isSaving || isFlipping || loading}
                  onClick={() => setCroppingComponentId('')}
                >
                  <IconClose width={16} height={16} />
                </SettingsComponentMenuButton>
              </TooltipWrapper>

              <SettingsComponentMenuHr />
              <CheckableSingle
                isSmall
                marginBottom={0}
                canWrap={false}
                options={[
                  {
                    label: 'Fit',
                    value: FIT,
                    isDisabled: isSaving,
                  },
                  {
                    label: 'Fill',
                    value: FILL,
                    isDisabled: isSaving,
                  },
                ]}
                name="mode"
                selected={mode}
                onSelect={value => setMode(value)}
              />
              {canFlip && (
                <>
                  <SettingsComponentMenuHr />
                  <SecondaryLoadingButton
                    type="button"
                    $isSmall
                    isLoading={isFlipping}
                    disabled={isFlipping || isSaving || loading}
                    onClick={() =>
                      handleFlipImage(
                        flip === FLIP_HORIZONTAL ? null : FLIP_HORIZONTAL
                      )
                    }
                  >
                    Flip
                  </SecondaryLoadingButton>
                </>
              )}

              <PrimaryButton
                type="button"
                $isSmall
                loading={isSaving}
                disabled={isSaving || loading || isFlipping}
                onClick={handleCropImage}
                data-selector="btn-save"
              >
                Save
              </PrimaryButton>
            </Styled.ImageCropSettingsFields>
          </Styled.ImageCropSettingsInner>
        </Styled.ImageCropSettings>
      )}

      <ComponentDimensions
        id={id}
        showHandle
        defaultValue={props.properties.dimensionData}
        onResizeStart={() => {
          setCanCrop(false);
        }}
        onResize={(width, height) => {
          setImagePickerSize(width, height);
        }}
        onResizeEnd={(width, height) => {
          setCanCrop(true);
          handleHeightUpdate({
            imagePicker: {
              ...imagePicker,
              width,
              height,
            },
            imageState,
            mode,
          });
        }}
      >
        <ImageContainer>
          {mode === FIT && (
            <ImageCropFit
              imageUrl={imageUrl}
              onImageLoad={initialiseImageState}
            />
          )}
          {mode === FILL && (
            <ImageCropFill
              canCrop={canCrop}
              imageState={imageState}
              imagePicker={imagePicker}
              imageUrl={imageUrl}
              onImageLoad={initialiseImageState}
              setImageState={setImageState}
            />
          )}
        </ImageContainer>

        {properties.showCaptionOnLesson && (
          <ComponentCaptionEdit
            caption={properties.caption}
            onChange={onCaptionChange}
          />
        )}
      </ComponentDimensions>
    </Styled.ImageCrop>
  );
};

export default ImageCrop;

const ImageCropFill = ({
  imageUrl,
  imageState,
  imagePicker,
  canCrop,
  onImageLoad,
  setImageState,
}: {
  imageUrl: string;
  imageState: ImageState;
  imagePicker: ImagePicker;
  canCrop: boolean;
  onImageLoad: (size: InitialImageSize) => void;
  setImageState: (state: ImageState) => void;
}) => {
  const editor = useRef<AvatarEditor | undefined>(undefined);

  const onImageChange = useCallback(() => {
    if (
      editor &&
      editor.current &&
      editor.current.state &&
      editor.current.state.image
    ) {
      const { x: editorX, y: editorY } = editor.current.state.image;
      if (
        (editorX !== imageState.x || editorY !== imageState.y) &&
        (editorX !== 0.5 || editorY !== 0.5)
      ) {
        setImageState({
          ...imageState,
          x: editorX,
          y: editorY,
        });
      }
    }
  }, [imageState, setImageState]);

  const updateZoom = useCallback(
    (newZoom: number) => {
      if (imageState.zoom !== newZoom) {
        setImageState({
          ...imageState,
          zoom: newZoom,
        });
      }
    },
    [imageState, setImageState]
  );

  const handleRangeChange = useCallback(
    (changeEvent: ChangeEvent<HTMLInputElement>) => {
      updateZoom(Number(changeEvent.target.value));
    },
    [updateZoom]
  );

  const zoomOut = useCallback(() => {
    const newZoom = clamp(imageState.zoom - RANGE_STEP, RANGE_MIN, RANGE_MAX);
    updateZoom(newZoom);
  }, [imageState.zoom, updateZoom]);

  const zoomIn = useCallback(() => {
    const newZoom = clamp(imageState.zoom + RANGE_STEP, RANGE_MIN, RANGE_MAX);
    updateZoom(newZoom);
  }, [imageState.zoom, updateZoom]);

  return (
    <>
      {canCrop && (
        <Styled.ImageCropWrapper>
          <img
            src={imageUrl}
            alt=""
            style={{
              opacity: 0,
            }}
            onLoad={e => onImageLoad(getInitialSizeValues(e))}
          />
        </Styled.ImageCropWrapper>
      )}
      <Styled.ImageCropperContainer>
        <Hammer
          options={{
            recognizers: {
              pinch: { enable: true },
            },
          }}
          onPinchIn={zoomOut}
          onPinchOut={zoomIn}
        >
          <div
            style={{
              position: 'relative',
              opacity: imagePicker.isReady ? 1 : 0,
              transition: 'opacity 400ms ease',
            }}
          >
            <AvatarEditor
              ref={el => {
                if (el) editor.current = el;
              }}
              border={0}
              image={imageUrl}
              width={imagePicker.width}
              height={imagePicker.height}
              position={{ x: imageState.x, y: imageState.y }}
              scale={imageState.zoom}
              onPositionChange={onImageChange}
              onImageChange={onImageChange}
            />
          </div>
        </Hammer>
      </Styled.ImageCropperContainer>

      <Styled.ImageCropZoom>
        <Range
          id="image-zoom"
          value={isNaN(imageState.zoom) ? RANGE_MIN : imageState.zoom}
          min={RANGE_MIN}
          max={RANGE_MAX}
          step={RANGE_STEP}
          onDecrement={zoomOut}
          onIncrement={zoomIn}
          handleChange={handleRangeChange}
        />
      </Styled.ImageCropZoom>
    </>
  );
};

const ImageCropFit = ({
  imageUrl,
  onImageLoad,
}: {
  imageUrl: string;
  onImageLoad: (sizes: InitialImageSize) => void;
}) => {
  const { imageDimensions, setContainerElement } = useImageDimensionEffect({
    imageUrl,
  });

  const isLoading = imageDimensions?.width === undefined;

  return (
    <Styled.ImageCropWrapperFit ref={setContainerElement}>
      <img
        src={imageUrl}
        alt=""
        style={{
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 400ms ease',
          width: imageDimensions?.width + 'px',
          height: imageDimensions?.height + 'px',
        }}
        onLoad={e => onImageLoad(getInitialSizeValues(e))}
      />
    </Styled.ImageCropWrapperFit>
  );
};
